import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { formatCurrency, getCurrencySymbol } from '../lib/currency';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Skeleton } from '../components/ui/skeleton';
import { Calendar } from '../components/ui/calendar';
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  PiggyBank,
  AlertCircle,
  ArrowRight,
  Plus,
  Upload,
  Camera,
  FileText,
  DollarSign,
  CreditCard,
  Building,
  Target,
  Globe,
  CalendarDays,
  Clock,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  BarChart3,
  PieChart as PieChartIcon,
  Activity,
  Landmark,
  Receipt
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  ComposedChart,
  Line,
  ReferenceLine,
  CartesianGrid
} from 'recharts';
import { motion } from 'framer-motion';

// Animation wrapper component
const FadeIn = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, delay, ease: 'easeOut' }}
    className={className}
  >
    {children}
  </motion.div>
);

// Glass card component
const GlassCard = ({ children, className = '', ...props }) => (
  <Card
    className={`bg-white/80 dark:bg-white/5 backdrop-blur-md border border-white/20 dark:border-white/10 shadow-sm hover:shadow-lg transition-shadow duration-200 ${className}`}
    {...props}
  >
    {children}
  </Card>
);

// KPI Card with sparkline
const KPICard = ({ title, value, delta, positive, icon: Icon, currency, loading, sparklineData }) => {
  const fmt = (v) => formatCurrency(v, currency);
  const formattedValue = fmt(value);
  const valueLength = formattedValue.length;
  
  // Dynamic font size: shorter values get larger fonts
  const getFontSizeClass = () => {
    if (valueLength <= 10) return 'text-2xl sm:text-3xl';
    if (valueLength <= 14) return 'text-xl sm:text-2xl';
    if (valueLength <= 18) return 'text-lg sm:text-xl';
    return 'text-base sm:text-lg';
  };
  
  return (
    <GlassCard className="p-4 sm:p-5" data-testid={`kpi-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg ${
            positive === true ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' :
            positive === false ? 'bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400' :
            'bg-[hsl(var(--brand-50))] text-[hsl(var(--brand-600))]'
          }`}>
            <Icon size={18} />
          </div>
          <span className="text-xs sm:text-sm text-[hsl(var(--muted-ink))] font-medium">{title}</span>
        </div>
        {delta !== undefined && delta !== null && (
          <Badge
            data-testid="kpi-delta-badge"
            className={`text-xs font-semibold border-0 ${
              positive === true
                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                : positive === false
                ? 'bg-rose-50 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
            }`}
          >
            <span className="flex items-center gap-0.5">
              {positive === true ? <ArrowUpRight size={12} /> :
               positive === false ? <ArrowDownRight size={12} /> :
               <Minus size={12} />}
              {Math.abs(delta).toFixed(1)}%
            </span>
          </Badge>
        )}
      </div>
      
      <div className="mb-2">
        {loading ? (
          <Skeleton className="h-8 w-32" />
        ) : (
          <span className={`${getFontSizeClass()} font-bold numbered tracking-tight block break-words`}>
            {formattedValue}
          </span>
        )}
      </div>
      
      {/* Sparkline */}
      {sparklineData && sparklineData.length > 0 && (
        <div className="h-10 mt-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparklineData}>
              <defs>
                <linearGradient id={`spark-${title}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={positive === false ? '#EF4444' : '#0EA5A4'} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={positive === false ? '#EF4444' : '#0EA5A4'} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="value"
                stroke={positive === false ? '#EF4444' : '#0EA5A4'}
                fill={`url(#spark-${title})`}
                strokeWidth={1.5}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </GlassCard>
  );
};

// Quick Action Button
const QuickAction = ({ icon: Icon, label, onClick, testId }) => (
  <Button
    variant="outline"
    className="flex flex-col items-center gap-2 h-auto py-4 px-3 bg-white/60 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 border-white/30 dark:border-white/10 backdrop-blur-sm transition-all duration-200 hover:shadow-md"
    onClick={onClick}
    data-testid={testId}
  >
    <div className="p-2 rounded-lg bg-[hsl(var(--brand-50))] text-[hsl(var(--brand-600))]">
      <Icon size={18} />
    </div>
    <span className="text-xs sm:text-sm font-medium text-[hsl(var(--ink-2))]">{label}</span>
  </Button>
);

// Account Balance Mini Card
const AccountBalanceCard = ({ name, balance, currency, type, trend }) => (
  <GlassCard className="p-3 sm:p-4" data-testid={`account-balance-card-${name.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-2 mb-2">
      <div className="p-1.5 rounded bg-slate-100 dark:bg-slate-800">
        {type === 'bank' ? <Building size={14} /> : type === 'cash' ? <Wallet size={14} /> : <CreditCard size={14} />}
      </div>
      <span className="text-xs text-[hsl(var(--muted-ink))] truncate">{name}</span>
    </div>
    <div className="flex items-end justify-between">
      <span className="text-lg sm:text-xl font-semibold numbered">{formatCurrency(balance, currency)}</span>
      {trend !== undefined && (
        <span className={`text-xs flex items-center ${trend >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
          {trend >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
          {Math.abs(trend).toFixed(1)}%
        </span>
      )}
    </div>
  </GlassCard>
);

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label, currency = 'USD' }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-md p-3 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700">
        <p className="text-xs text-[hsl(var(--muted-ink))] mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm font-semibold" style={{ color: entry.color }}>
            {entry.name}: {formatCurrency(entry.value, currency)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const DashboardStandard = () => {
  const { company, currency } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [cashFlowData, setCashFlowData] = useState([]);
  const [timeRange, setTimeRange] = useState('30d');
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [loans, setLoans] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    loadDashboard();
    
    const handleRefresh = () => loadDashboard();
    window.addEventListener('transaction-created', handleRefresh);
    return () => window.removeEventListener('transaction-created', handleRefresh);
  }, []);

  const loadDashboard = async () => {
    try {
      const [dashRes, cashFlowRes, accountsRes, categoriesRes, receivablesRes, loansRes] = await Promise.all([
        api.get('/dashboard'),
        api.get('/dashboard/cash-flow?months=6'),
        api.get('/accounts').catch(() => ({ data: [] })),
        api.get('/categories').catch(() => ({ data: [] })),
        api.get('/receivables').catch(() => ({ data: [] })),
        api.get('/loans').catch(() => ({ data: [] }))
      ]);
      
      setDashboardData(dashRes.data);
      setCashFlowData(cashFlowRes.data);
      setAccounts(accountsRes.data || []);
      setCategories(categoriesRes.data || []);
      setReceivables(receivablesRes.data || []);
      setLoans(loansRes.data || []);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (amount) => formatCurrency(amount, currency);
  const kpis = dashboardData?.kpis || {};

  // Generate sparkline data
  const generateSparkline = (baseValue, trend = 1) => {
    return Array.from({ length: 7 }, (_, i) => ({
      value: baseValue * (0.85 + Math.random() * 0.3) * (1 + (trend * i * 0.02))
    }));
  };

  // Calculate category breakdown for donut chart
  const categoryData = useMemo(() => {
    if (!dashboardData?.recent_transactions) return [];
    
    const categoryTotals = {};
    dashboardData.recent_transactions.forEach(txn => {
      if (txn.transaction_type === 'expense' && txn.category) {
        const catName = txn.category.name || 'Uncategorized';
        categoryTotals[catName] = (categoryTotals[catName] || 0) + parseFloat(txn.amount || 0);
      }
    });
    
    const sorted = Object.entries(categoryTotals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
    
    return sorted.map(([name, value], i) => ({
      name,
      value,
      color: ['#0EA5A4', '#34D399', '#60A5FA', '#F59E0B', '#94A3B8'][i]
    }));
  }, [dashboardData]);

  // Calculate AR aging
  const arAging = useMemo(() => {
    const buckets = { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
    const today = new Date();
    
    receivables.filter(r => r.status !== 'paid').forEach(r => {
      const dueDate = new Date(r.due_date);
      const daysOverdue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
      const amount = parseFloat(r.amount_due || 0) - parseFloat(r.amount_paid || 0);
      
      if (daysOverdue <= 30) buckets['0-30'] += amount;
      else if (daysOverdue <= 60) buckets['31-60'] += amount;
      else if (daysOverdue <= 90) buckets['61-90'] += amount;
      else buckets['90+'] += amount;
    });
    
    const total = Object.values(buckets).reduce((a, b) => a + b, 0);
    return { buckets, total };
  }, [receivables]);

  // Revenue vs Expenses data
  const revenueExpenseData = useMemo(() => {
    return cashFlowData.map(d => ({
      period: d.month,
      revenue: d.inflow || 0,
      expenses: d.outflow || 0
    }));
  }, [cashFlowData]);

  // Profit margin data
  const profitMarginData = useMemo(() => {
    return cashFlowData.map(d => ({
      month: d.month,
      margin: d.inflow > 0 ? ((d.inflow - d.outflow) / d.inflow * 100) : 0
    }));
  }, [cashFlowData]);

  // Budget vs Actual (mock data for demo)
  const budgetActualData = useMemo(() => {
    return cashFlowData.map(d => ({
      month: d.month,
      budget: (d.outflow || 0) * 1.1,
      actual: d.outflow || 0,
      variance: ((d.outflow || 0) * 1.1) - (d.outflow || 0)
    }));
  }, [cashFlowData]);

  // Currency exposure data
  const currencyExposure = useMemo(() => {
    const exposure = {};
    accounts.forEach(acc => {
      const curr = acc.currency || 'USD';
      exposure[curr] = (exposure[curr] || 0) + parseFloat(acc.current_balance || 0);
    });
    return Object.entries(exposure).map(([name, value], i) => ({
      name,
      value: Math.abs(value),
      color: ['#0EA5A4', '#3B82F6', '#22C55E', '#F59E0B', '#64748B'][i % 5]
    }));
  }, [accounts]);

  // Upcoming payments
  const upcomingPayments = useMemo(() => {
    const payments = [];
    
    loans.filter(l => l.status === 'active' && l.next_payment_date).forEach(l => {
      payments.push({
        type: 'loan',
        title: `Loan Payment - ${l.loan_number}`,
        amount: l.monthly_payment,
        date: l.next_payment_date,
        icon: Landmark
      });
    });
    
    receivables.filter(r => r.status !== 'paid').forEach(r => {
      payments.push({
        type: 'receivable',
        title: `Invoice Due - ${r.invoice_number || 'INV'}`,
        amount: (r.amount_due || 0) - (r.amount_paid || 0),
        date: r.due_date,
        icon: Receipt
      });
    });
    
    return payments.sort((a, b) => new Date(a.date) - new Date(b.date)).slice(0, 5);
  }, [loans, receivables]);

  // Bank/cash accounts
  const bankAccounts = useMemo(() => {
    return accounts.filter(a => ['bank', 'cash', 'mobile_money'].includes(a.account_subtype)).slice(0, 4);
  }, [accounts]);

  const greeting = new Date().getHours() < 12 ? 'Good morning' : new Date().getHours() < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <TooltipProvider>
      <div className="space-y-6 pb-8" data-testid="dashboard-root">
        {/* Hero Header with Gradient */}
        <FadeIn>
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[hsl(189,100%,97%)] via-[hsl(188,78%,78%,0.45)] to-[hsl(210,20%,98%)] p-6 sm:p-8 mb-6">
            <div className="relative z-10">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[hsl(var(--ink-1))] tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
                {greeting}!
              </h1>
              <p className="text-sm sm:text-base text-[hsl(var(--muted-ink))] mt-1">
                Here&apos;s your financial overview for {company?.name || 'your business'}
              </p>
            </div>
            {/* Decorative blur circles */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[hsl(var(--brand-300))] rounded-full filter blur-3xl opacity-20 -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-1/4 w-48 h-48 bg-[hsl(var(--brand-500))] rounded-full filter blur-3xl opacity-10" />
          </div>
        </FadeIn>

        {/* KPI Row */}
        <FadeIn delay={0.05}>
          <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="kpi-row">
            <KPICard
              title="Revenue"
              value={kpis.revenue?.value || 0}
              delta={kpis.revenue?.delta}
              positive={kpis.revenue?.positive}
              icon={TrendingUp}
              currency={currency}
              loading={loading}
              sparklineData={generateSparkline(kpis.revenue?.value || 1000, 1)}
            />
            <KPICard
              title="Expenses"
              value={kpis.expenses?.value || 0}
              delta={kpis.expenses?.delta}
              positive={kpis.expenses?.positive}
              icon={TrendingDown}
              currency={currency}
              loading={loading}
              sparklineData={generateSparkline(kpis.expenses?.value || 800, -1)}
            />
            <KPICard
              title="Net Profit"
              value={kpis.net_profit?.value || 0}
              positive={kpis.net_profit?.positive}
              icon={PiggyBank}
              currency={currency}
              loading={loading}
              sparklineData={generateSparkline(kpis.net_profit?.value || 200, 1)}
            />
            <KPICard
              title="Cash Position"
              value={kpis.cash_position?.value || 0}
              icon={Wallet}
              currency={currency}
              loading={loading}
              sparklineData={generateSparkline(kpis.cash_position?.value || 5000, 1)}
            />
            <KPICard
              title="Loans Outstanding"
              value={dashboardData?.loan_summary?.total_outstanding || 0}
              positive={false}
              icon={Landmark}
              currency={currency}
              loading={loading}
              sparklineData={generateSparkline(dashboardData?.loan_summary?.total_outstanding || 0, -1)}
            />
          </section>
        </FadeIn>

        {/* Charts Row */}
        <FadeIn delay={0.1}>
          <section className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
            {/* Cash Flow Chart */}
            <GlassCard className="lg:col-span-7 p-4 sm:p-6" data-testid="cashflow-section">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-[hsl(var(--brand-50))] text-[hsl(var(--brand-600))]">
                    <Activity size={18} />
                  </div>
                  <h3 className="font-semibold text-[hsl(var(--ink-1))]">Cash Flow Trend</h3>
                </div>
                <Tabs value={timeRange} onValueChange={setTimeRange} data-testid="cashflow-time-tabs">
                  <TabsList className="bg-slate-100/80 dark:bg-slate-800/80">
                    {['7d', '30d', '90d', '12m'].map(v => (
                      <TabsTrigger key={v} value={v} className="text-xs" data-testid={`cashflow-time-toggle-${v}`}>
                        {v.toUpperCase()}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>
              <div className="h-64 sm:h-72" data-testid="cashflow-area-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={cashFlowData}>
                    <defs>
                      <linearGradient id="inflowGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="outflowGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ef4444" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#ef4444" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" tickFormatter={(v) => `${getCurrencySymbol(currency)}${(v/1000).toFixed(0)}k`} />
                    <RechartsTooltip content={<CustomTooltip currency={currency} />} />
                    <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                    <Area type="monotone" dataKey="inflow" name="Inflow" stroke="#10b981" fill="url(#inflowGrad)" strokeWidth={2} />
                    <Area type="monotone" dataKey="outflow" name="Outflow" stroke="#ef4444" fill="url(#outflowGrad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </GlassCard>

            {/* Revenue vs Expenses */}
            <GlassCard className="lg:col-span-5 p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-[hsl(var(--brand-50))] text-[hsl(var(--brand-600))]">
                  <BarChart3 size={18} />
                </div>
                <h3 className="font-semibold text-[hsl(var(--ink-1))]">Revenue vs Expenses</h3>
              </div>
              <div className="h-64 sm:h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueExpenseData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                    <RechartsTooltip content={<CustomTooltip currency={currency} />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="revenue" name="Revenue" fill="#2563EB" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expenses" name="Expenses" fill="#EF4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </GlassCard>
          </section>
        </FadeIn>

        {/* Quick Actions & Account Balances */}
        <FadeIn delay={0.15}>
          <section className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
            {/* Quick Actions */}
            <GlassCard className="lg:col-span-5 p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-[hsl(var(--brand-50))] text-[hsl(var(--brand-600))]">
                  <Target size={18} />
                </div>
                <h3 className="font-semibold text-[hsl(var(--ink-1))]">Quick Actions</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <QuickAction icon={Plus} label="Add Transaction" onClick={() => window.dispatchEvent(new CustomEvent('open-add-transaction'))} testId="quick-action-add-transaction" />
                <QuickAction icon={Upload} label="Import Bank" onClick={() => window.location.href = '/bank-import'} testId="quick-action-import-bank" />
                <QuickAction icon={Camera} label="Scan Receipt" onClick={() => window.location.href = '/receipt-scanner'} testId="quick-action-scan-receipt" />
                <QuickAction icon={FileText} label="View Reports" onClick={() => window.location.href = '/reports'} testId="quick-action-view-reports" />
              </div>
            </GlassCard>

            {/* Account Balances */}
            <GlassCard className="lg:col-span-7 p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-[hsl(var(--brand-50))] text-[hsl(var(--brand-600))]">
                    <CreditCard size={18} />
                  </div>
                  <h3 className="font-semibold text-[hsl(var(--ink-1))]">Account Balances</h3>
                </div>
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => window.location.href = '/settings'}>
                  View All <ChevronRight size={14} />
                </Button>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {bankAccounts.length > 0 ? bankAccounts.map(acc => (
                  <AccountBalanceCard
                    key={acc.id}
                    name={acc.name}
                    balance={acc.current_balance || 0}
                    currency={acc.currency || currency}
                    type={acc.account_subtype}
                  />
                )) : (
                  <div className="col-span-full text-center py-8 text-[hsl(var(--muted-ink))]">
                    <Wallet className="mx-auto mb-2 opacity-50" size={24} />
                    <p className="text-sm">No accounts set up yet</p>
                  </div>
                )}
              </div>
            </GlassCard>
          </section>
        </FadeIn>

        {/* Analytics Row: Categories, AR Aging, Upcoming */}
        <FadeIn delay={0.2}>
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {/* Top Categories Donut */}
            <GlassCard className="p-4 sm:p-6" data-testid="top-categories-chart">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-[hsl(var(--brand-50))] text-[hsl(var(--brand-600))]">
                  <PieChartIcon size={18} />
                </div>
                <h3 className="font-semibold text-[hsl(var(--ink-1))]">Top Expense Categories</h3>
              </div>
              <div className="h-48">
                {categoryData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={categoryData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {categoryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip content={<CustomTooltip currency={currency} />} />
                      <Legend wrapperStyle={{ fontSize: '11px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-[hsl(var(--muted-ink))]">
                    <p className="text-sm">No expense data yet</p>
                  </div>
                )}
              </div>
            </GlassCard>

            {/* Receivables Health / AR Aging */}
            <GlassCard className="p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-[hsl(var(--brand-50))] text-[hsl(var(--brand-600))]">
                  <DollarSign size={18} />
                </div>
                <h3 className="font-semibold text-[hsl(var(--ink-1))]">Receivables Health</h3>
              </div>
              <div className="space-y-4">
                {[
                  { label: '0-30 days', key: '0-30', color: 'bg-blue-500' },
                  { label: '31-60 days', key: '31-60', color: 'bg-amber-500' },
                  { label: '61-90 days', key: '61-90', color: 'bg-orange-500' },
                  { label: '90+ days', key: '90+', color: 'bg-rose-500' },
                ].map(({ label, key, color }) => {
                  const value = arAging.buckets[key] || 0;
                  const percent = arAging.total > 0 ? (value / arAging.total * 100) : 0;
                  return (
                    <div key={key} data-testid={`ar-aging-${key.replace('+', '-plus')}`}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-[hsl(var(--muted-ink))]">{label}</span>
                        <span className="font-medium">{fmt(value)}</span>
                      </div>
                      <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${percent}%` }} />
                      </div>
                    </div>
                  );
                })}
                <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex justify-between text-sm">
                    <span className="text-[hsl(var(--muted-ink))]">Total Outstanding</span>
                    <span className="font-semibold">{fmt(arAging.total)}</span>
                  </div>
                </div>
              </div>
            </GlassCard>

            {/* Upcoming Payments */}
            <GlassCard className="p-4 sm:p-6" data-testid="payments-timeline">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-[hsl(var(--brand-50))] text-[hsl(var(--brand-600))]">
                  <Clock size={18} />
                </div>
                <h3 className="font-semibold text-[hsl(var(--ink-1))]">Upcoming Payments</h3>
              </div>
              <ScrollArea className="h-48">
                {upcomingPayments.length > 0 ? (
                  <div className="space-y-3">
                    {upcomingPayments.map((payment, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                        <div className={`p-2 rounded-lg ${
                          payment.type === 'loan' ? 'bg-blue-50 text-blue-600' : 'bg-emerald-50 text-emerald-600'
                        }`}>
                          <payment.icon size={14} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{payment.title}</p>
                          <p className="text-xs text-[hsl(var(--muted-ink))]">
                            {new Date(payment.date).toLocaleDateString()}
                          </p>
                        </div>
                        <span className="text-sm font-semibold numbered">{fmt(payment.amount)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-[hsl(var(--muted-ink))]">
                    <div className="text-center">
                      <CalendarDays className="mx-auto mb-2 opacity-50" size={24} />
                      <p className="text-sm">No upcoming payments</p>
                    </div>
                  </div>
                )}
              </ScrollArea>
            </GlassCard>
          </section>
        </FadeIn>

        {/* Advanced Analytics Row */}
        <FadeIn delay={0.25}>
          <section className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
            {/* Profit Margin Trends */}
            <GlassCard className="lg:col-span-4 p-4 sm:p-6" data-testid="profit-margin-chart">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
                  <TrendingUp size={18} />
                </div>
                <h3 className="font-semibold text-[hsl(var(--ink-1))]">Profit Margin Trend</h3>
              </div>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={profitMarginData}>
                    <defs>
                      <linearGradient id="marginGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22C55E" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#22C55E" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `${v.toFixed(0)}%`} />
                    <RechartsTooltip formatter={(v) => `${v.toFixed(1)}%`} />
                    <Area type="monotone" dataKey="margin" name="Margin" stroke="#22C55E" fill="url(#marginGrad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </GlassCard>

            {/* Budget vs Actual */}
            <GlassCard className="lg:col-span-4 p-4 sm:p-6" data-testid="budget-actual-chart">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
                  <Target size={18} />
                </div>
                <h3 className="font-semibold text-[hsl(var(--ink-1))]">Budget vs Actual</h3>
              </div>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={budgetActualData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                    <RechartsTooltip content={<CustomTooltip currency={currency} />} />
                    <Bar dataKey="actual" name="Actual" fill="#475569" radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="budget" name="Budget" stroke="#0EA5E9" strokeWidth={2} dot={{ fill: '#0EA5E9', r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </GlassCard>

            {/* Currency Exposure */}
            <GlassCard className="lg:col-span-4 p-4 sm:p-6" data-testid="currency-exposure-chart">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-[hsl(var(--brand-50))] text-[hsl(var(--brand-600))]">
                  <Globe size={18} />
                </div>
                <h3 className="font-semibold text-[hsl(var(--ink-1))]">Currency Exposure</h3>
              </div>
              <div className="h-48">
                {currencyExposure.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={currencyExposure}
                        cx="50%"
                        cy="50%"
                        innerRadius={35}
                        outerRadius={65}
                        paddingAngle={3}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {currencyExposure.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(v) => formatCurrency(v, 'USD')} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-[hsl(var(--muted-ink))]">
                    <p className="text-sm">Single currency</p>
                  </div>
                )}
              </div>
            </GlassCard>
          </section>
        </FadeIn>

        {/* Bottom Row: Calendar + Transactions + Alerts */}
        <FadeIn delay={0.3}>
          <section className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
            {/* Financial Calendar */}
            <GlassCard className="lg:col-span-4 p-4 sm:p-6" data-testid="financial-calendar">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-[hsl(var(--brand-50))] text-[hsl(var(--brand-600))]">
                  <CalendarDays size={18} />
                </div>
                <h3 className="font-semibold text-[hsl(var(--ink-1))]">Financial Calendar</h3>
              </div>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                className="rounded-md border-0 mx-auto"
              />
            </GlassCard>

            {/* Recent Transactions */}
            <GlassCard className="lg:col-span-5 p-4 sm:p-6" data-testid="transactions-table">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-[hsl(var(--brand-50))] text-[hsl(var(--brand-600))]">
                    <Receipt size={18} />
                  </div>
                  <h3 className="font-semibold text-[hsl(var(--ink-1))]">Recent Transactions</h3>
                </div>
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => window.location.href = '/transactions'}>
                  View All <ArrowRight size={14} className="ml-1" />
                </Button>
              </div>
              <ScrollArea className="h-64">
                {dashboardData?.recent_transactions?.length > 0 ? (
                  <div className="space-y-2">
                    {dashboardData.recent_transactions.slice(0, 6).map((txn, i) => (
                      <div key={txn.id || i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                        <div className={`p-2 rounded-lg ${
                          txn.transaction_type === 'revenue' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
                        }`}>
                          {txn.transaction_type === 'revenue' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{txn.description || 'Transaction'}</p>
                          <p className="text-xs text-[hsl(var(--muted-ink))]">
                            {txn.category?.name || 'Uncategorized'} • {new Date(txn.date).toLocaleDateString()}
                          </p>
                        </div>
                        <span className={`text-sm font-semibold numbered ${
                          txn.transaction_type === 'revenue' ? 'text-emerald-600' : 'text-rose-600'
                        }`}>
                          {txn.transaction_type === 'revenue' ? '+' : '-'}{fmt(txn.display_amount || txn.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-[hsl(var(--muted-ink))]">
                    <div className="text-center">
                      <Receipt className="mx-auto mb-2 opacity-50" size={24} />
                      <p className="text-sm">No transactions yet</p>
                      <Button variant="link" size="sm" className="mt-2" onClick={() => window.dispatchEvent(new CustomEvent('open-add-transaction'))}>
                        Add your first transaction
                      </Button>
                    </div>
                  </div>
                )}
              </ScrollArea>
            </GlassCard>

            {/* Action Required */}
            <GlassCard className="lg:col-span-3 p-4 sm:p-6" data-testid="action-required-list">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-amber-50 text-amber-600">
                  <AlertCircle size={18} />
                </div>
                <h3 className="font-semibold text-[hsl(var(--ink-1))]">Action Required</h3>
              </div>
              <ScrollArea className="h-64">
                {dashboardData?.action_items?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboardData.action_items.map((item, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-lg border-l-4 ${
                          item.urgency === 'high'
                            ? 'bg-rose-50 border-rose-500 dark:bg-rose-900/20'
                            : 'bg-amber-50 border-amber-500 dark:bg-amber-900/20'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{item.title}</p>
                            <p className="text-xs text-[hsl(var(--muted-ink))] mt-1 line-clamp-2">{item.description}</p>
                          </div>
                          <Badge className={`text-xs shrink-0 ${
                            item.urgency === 'high' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {item.urgency}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-[hsl(var(--muted-ink))]">
                    <div className="text-center">
                      <div className="w-12 h-12 mx-auto bg-emerald-50 rounded-full flex items-center justify-center mb-3">
                        <CalendarDays size={24} className="text-emerald-600" />
                      </div>
                      <p className="text-sm">You&apos;re all caught up! ✓</p>
                    </div>
                  </div>
                )}
              </ScrollArea>
            </GlassCard>
          </section>
        </FadeIn>
      </div>
    </TooltipProvider>
  );
};

export default DashboardStandard;
